import React, { useState } from 'react';
import { useT } from '../lib/i18n';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface Props {
  user: User | null;
  onClose: () => void;
}

export function UpgradeModal({ user, onClose }: Props) {
  const [loading, setLoading] = useState<'monthly' | 'yearly' | null>(null);
  const [error, setError] = useState('');
  const { t } = useT();
  const IS_LOCKED = false;

  const checkout = async (plan: 'monthly' | 'yearly') => {
    if (!user) return;
    setLoading(plan);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('No active session');

      const base = process.env.NODE_ENV === 'development'
        ? 'http://localhost:3001'
        : '';
      const res = await fetch(`${base}/api/whop/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ plan }),
      });

      // Guard against server returning HTML (e.g. 404 page)
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(t('upgrade.errorCheckout'));
      }

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('upgrade.errorCheckout'));
      window.location.href = data.url;
    } catch (err: any) {
      setError(err.message);
      setLoading(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box upgrade-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>

        <div className="upgrade-header">
          <span className="upgrade-icon">◈</span>
          <h2 className="upgrade-title">{t('upgrade.title')}</h2>
          <p className="upgrade-sub">{t('upgrade.subtitle')}</p>
        </div>

        {IS_LOCKED ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', margin: '1rem 0 2rem 0' }}>

            <div className="upgrade-launch-banner" style={{ margin: 0, fontSize: '0.9rem', padding: '1rem' }}>
              🚀 {t('upgrade.comingSoonMessage') || 'LA VERSIÓN PRO ESTARÁ DISPONIBLE EL 1 DE ABRIL'}
            </div>

            <p className="upgrade-sub" style={{ textAlign: 'center', lineHeight: '1.6', margin: '0 auto', maxWidth: '340px' }}>
              {t('upgrade.comingSoonSub') || 'Estamos preparando las mejores herramientas de análisis con IA para tu grind.'}
            </p>

            <button
              className="upgrade-plan-btn upgrade-plan-featured"
              onClick={onClose}
              style={{ width: '100%', padding: '1rem', marginTop: '0.5rem' }}
            >
              <span style={{
                fontFamily: 'var(--mono)',
                fontSize: '0.85rem',
                fontWeight: 700,
                color: 'var(--text-hi)',
                letterSpacing: '1px',
                textTransform: 'uppercase'
              }}>
                {t('upgrade.backSoon') || 'Volver a la app'}
              </span>
            </button>
          </div>
        ) : (
          <div>
            <div className="upgrade-features">
              <div className="upgrade-feature">
                <span className="uf-icon">✓</span>
                <span><strong>Coach IA</strong> — {t('upgrade.featureAi')}</span>
              </div>
              <div className="upgrade-feature">
                <span className="uf-icon">✓</span>
                <span>{t('upgrade.featureBench')}</span>
              </div>
              <div className="upgrade-feature">
                <span className="uf-icon">✓</span>
                <span>{t('upgrade.featureSync')}</span>
              </div>
              <div className="upgrade-feature upgrade-feature-muted">
                <span className="uf-icon">✓</span>
                <span>{t('upgrade.featureFuture')}</span>
              </div>
            </div>

            <div className="upgrade-launch-banner">
              {t('upgrade.launchBanner')}
            </div>

            <div className="upgrade-plans">
              <button
                className={`upgrade-plan-btn ${loading === 'monthly' ? 'loading' : ''}`}
                onClick={() => checkout('monthly')}
                disabled={loading !== null}
              >
                <div className="up-launch-price">
                  <span className="up-amount launch">{t('upgrade.monthlyAmount')}</span>
                  <span className="up-period">{t('upgrade.monthlyPeriod')}</span>
                </div>
                <div className="up-regular-price">{t('upgrade.monthlyRegular')}</div>
                <div className="up-label">{t('upgrade.monthlyLabel')}</div>
                {loading === 'monthly' && <div className="up-spinner" />}
              </button>

              <button
                className={`upgrade-plan-btn upgrade-plan-featured ${loading === 'yearly' ? 'loading' : ''}`}
                onClick={() => checkout('yearly')}
                disabled={loading !== null}
              >
                <div className="up-badge">{t('upgrade.yearlyBadge')} · 2 MESES GRATIS</div>
                <div className="up-launch-price">
                  <span className="up-amount launch">{t('upgrade.yearlyAmount')}</span>
                  <span className="up-period">{t('upgrade.yearlyPeriod')}</span>
                </div>
                <div className="up-regular-price">{t('upgrade.yearlyRegular')}</div>
                <div className="up-label">{t('upgrade.yearlyLabel')}</div>
                {loading === 'yearly' && <div className="up-spinner" />}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="auth-message err" style={{ marginTop: '1rem' }}>
            {error}
          </div>
        )}

        <p className="upgrade-fine">
          {t('upgrade.cancel')}
          <br />
          {t('upgrade.support')} <a href="mailto:support@grindlog.app">support@grindlog.app</a>
        </p>
      </div>
    </div>
  );
}